/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { DraggableTab } from '@components/molecules'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import Tabs from '@mui/material/Tabs'
import { mono, boSize, boAdmin } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppSelector,
	useAppDispatch,
	configActions,
	TabType,
	ConfigStateType,
} from '@redux/index'
import { useNavigate } from 'react-router-dom'

export const MainNavTabs = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const selectedTabIdx: string = useAppSelector(state => state.config.selectedTabIdx)
	const tabList: TabType[] = useAppSelector(state => state.config.tabList)

	// variables
	const variant = tabList.length === 1 ? 'standard' : 'scrollable'

	const onTabClick = React.useCallback(
		(event: React.SyntheticEvent, newValue: string) => {
			const newSelectedTabIdx: string = parseInt(newValue.replace('Tab ', '')).toString()
			const newSelectedTab: TabType = tabList[parseInt(newSelectedTabIdx) - 1]

			navigate(newSelectedTab.path)
		},
		[tabList],
	)

	const onDragEnd = React.useCallback(
		(result: any) => {
			const selectedTabId: string = tabList[parseInt(selectedTabIdx) - 1].id
			const destinationIdx: number = result.destination.index
			const newTabs: TabType[] = Array.from([...tabList])
			const draggedTab: TabType = newTabs.splice(result.source.index, 1)[0]
			newTabs.splice(destinationIdx, 0, draggedTab)

			const newSelectedTabIdx: number = newTabs.findIndex(
				(aTab: TabType) => aTab.id === selectedTabId,
			)

			reduxDispatch(configActions.TAB_LIST([...newTabs]))
			reduxDispatch(configActions.SELECTED_TAB_IDX((newSelectedTabIdx + 1).toString()))
		},
		[tabList],
	)

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<View css={style} className={mode === 'admin' ? 'admin' : ''}>
				<Droppable droppableId="1" direction="horizontal">
					{droppableProvided => (
						<View
							className="droppableInnerView"
							ref={droppableProvided.innerRef}
							{...droppableProvided.droppableProps}
						>
							<Tabs
								className="tabList"
								value={`Tab ${selectedTabIdx}`}
								onChange={onTabClick}
								textColor="inherit"
								scrollButtons="auto"
								allowScrollButtonsMobile
								aria-label="Draggable Tabs"
								variant={variant}
								TabIndicatorProps={{
									style:
										selectedTabIdx === '1'
											? { background: 'transparent' }
											: {
													background: mode === 'admin' ? boAdmin.accent : mono.ink,
													height: 1,
											  },
								}}
							>
								{tabList.map((aTab: TabType, idx: number) => {
									return (
										<DraggableTab
											key={aTab.id}
											id={aTab.id}
											path={aTab.path}
											i18n={aTab.i18n}
											index={idx}
											label={aTab.name}
											value={`Tab ${idx + 1}`}
										/>
									)
								})}
								{droppableProvided ? droppableProvided.placeholder : null}
							</Tabs>
						</View>
					)}
				</Droppable>
			</View>
		</DragDropContext>
	)
}

const style = css({
	zIndex: 3,
	flexGrow: 1,
	minWidth: 0,
	height: '100%',
	minHeight: '100%',
	paddingLeft: 12,

	'.droppableInnerView': {
		display: 'flex',
		alignItems: 'stretch',
		height: '100%',
		minHeight: '100%',
		minWidth: 0,
		maxWidth: '100%',
	},

	'.tabList': {
		display: 'flex',
		alignItems: 'stretch',
		height: boSize.navH - 1,
		minHeight: boSize.navH - 1,
		// 사이드바 240 을 가정하던 calc(100vw - 17rem) 을 걷어냈다 — 부모(navInner) 폭 안에서 스크롤한다
		maxWidth: '100%',

		'.MuiTabs-scroller': { height: '100%' },
		'.MuiTabs-flexContainer': { height: '100%', alignItems: 'stretch' },
		'.MuiTabs-scrollButtons': { color: mono.faint, width: 28 },
		'.MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.2 },
	},

	'&.admin .tabList': {
		'.MuiTabs-scrollButtons': { color: boAdmin.text },
		'.boTab': { color: boAdmin.text },
		'.boTab.Mui-selected, .boTab:hover': { color: boAdmin.textOn },
		'.removeButton .removeIcon': { color: boAdmin.text },
		'.removeButton:hover': { background: boAdmin.barHover },
		'.removeButton:hover .removeIcon': { color: boAdmin.textOn },
	},
})
